import { assert } from '@sparkdotfi/common-universal'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Timeframe } from '../defaults'
import { TimeframeButtons } from './TimeframeButtons'
import { DelayedComponent } from '@/ui/atoms/delayed-component/DelayedComponent'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/atoms/tabs/Tabs'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import { useElementSize } from '@/ui/utils/useElementSize'

export interface ChartTabComponentProps {
  height: number
  width: number
}

type ChartTabDefinition<C> = C extends React.ComponentType<infer P>
  ? P extends ChartTabComponentProps
    ? {
        component: C
        props: Omit<P, keyof ChartTabComponentProps>
        id: string
        label: string
        isPending: boolean
        isError: boolean
        headerExtra?: React.ReactNode
        timeframeOptions?: {
          selectedTimeframe: Timeframe
          setSelectedTimeframe: (timeframe: Timeframe) => void
          availableTimeframes: Timeframe[]
        }
        minHeight?: number
      }
    : never
  : never

declare const __CHART_TAB_OPAQUE_TYPE__: unique symbol

export interface ChartTab {
  component: React.ComponentType<ChartTabComponentProps>
  props: ChartTabComponentProps
  id: string
  label: string
  readonly [__CHART_TAB_OPAQUE_TYPE__]: true
  isPending: boolean
  isError: boolean
  headerExtra?: React.ReactNode
  timeframeOptions?: {
    selectedTimeframe: Timeframe
    setSelectedTimeframe: (timeframe: Timeframe) => void
    availableTimeframes: Timeframe[]
  }
  minHeight?: number
}

export function createChartTab<C>(chart: ChartTabDefinition<C>): ChartTab {
  return chart as unknown as ChartTab
}

interface ChartTabsPanelProps {
  height?: number
  tabs: ChartTab[]
  minHeight?: number
  selectedTabId?: string
  onSelectedTabIdChange?: (tabId: string) => void
}

export function ChartTabsPanel({
  tabs,
  height = 300,
  minHeight = 380,
  selectedTabId: controlledSelectedTabId,
  onSelectedTabIdChange,
}: ChartTabsPanelProps) {
  const firstTab = tabs[0]
  assert(firstTab, 'ChartTabsPanel: at least 1 tab is required')

  const isControlled = controlledSelectedTabId !== undefined
  const [uncontrolledSelectedTabId, setUncontrolledSelectedTabId] = useState(firstTab.id)
  const effectiveSelectedTabId = isControlled ? controlledSelectedTabId : uncontrolledSelectedTabId
  const selectedTabExists = tabs.some((tab) => tab.id === effectiveSelectedTabId)
  const selectedTabId = selectedTabExists ? effectiveSelectedTabId : firstTab.id
  const selectedTab = tabs.find((tab) => tab.id === selectedTabId) ?? firstTab

  useEffect(() => {
    if (selectedTabExists) {
      return
    }

    if (!isControlled) {
      setUncontrolledSelectedTabId(firstTab.id)
      return
    }

    onSelectedTabIdChange?.(firstTab.id)
  }, [firstTab.id, isControlled, onSelectedTabIdChange, selectedTabExists])

  function handleTabChange(nextTabId: string) {
    if (!isControlled) {
      setUncontrolledSelectedTabId(nextTabId)
    }
    onSelectedTabIdChange?.(nextTabId)
  }

  const isSm = useBreakpoint('sm')
  const effectiveMinHeight = selectedTab.minHeight ?? minHeight

  if (tabs.length === 1) {
    const { timeframeOptions } = firstTab
    const headerExtra =
      timeframeOptions || firstTab.headerExtra ? (
        <div className="flex w-full flex-col items-center gap-2 lg:w-auto lg:flex-row lg:gap-2">
          {timeframeOptions && (
            <TimeframeButtons
              onTimeframeChange={timeframeOptions.setSelectedTimeframe}
              selectedTimeframe={timeframeOptions.selectedTimeframe}
              availableTimeframes={timeframeOptions.availableTimeframes}
              className="w-full lg:w-auto"
            />
          )}
          {firstTab.headerExtra}
        </div>
      ) : null

    return (
      <Panel
        className="flex w-full flex-1 flex-col justify-between self-stretch overflow-hidden"
        style={{ minHeight: isSm ? effectiveMinHeight : undefined }}
      >
        <div className="grid grid-cols-1 grid-rows-2 items-center gap-4 lg:grid-cols-2 lg:grid-rows-1">
          <div className="typography-heading-5 flex items-center gap-1">{firstTab.label}</div>
          {headerExtra}
        </div>

        <div className="flex w-full grow flex-col items-center justify-start sm:grow sm:justify-center">
          <ChartPanel {...firstTab} height={height} />
        </div>
      </Panel>
    )
  }

  return (
    <Panel
      className="flex w-full flex-1 flex-col justify-between self-stretch overflow-hidden"
      style={{ minHeight: isSm ? effectiveMinHeight : undefined }}
    >
      <Tabs value={selectedTabId} onValueChange={handleTabChange} className="flex flex-1 flex-col">
        <div className="flex flex-col flex-wrap items-center gap-2 lg:flex-row lg:justify-between lg:gap-1">
          <TabsList className="w-full lg:w-auto" size="s">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className="typography-label-3 px-1 md:px-1.5 xl:px-5">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {(selectedTab.timeframeOptions || selectedTab.headerExtra) && (
            <div className="flex w-full flex-col items-center gap-2 lg:w-auto lg:flex-row lg:gap-2">
              {selectedTab.timeframeOptions && (
                <TimeframeButtons
                  onTimeframeChange={selectedTab.timeframeOptions.setSelectedTimeframe}
                  selectedTimeframe={selectedTab.timeframeOptions.selectedTimeframe}
                  availableTimeframes={selectedTab.timeframeOptions.availableTimeframes}
                  className="w-full lg:w-auto"
                />
              )}
              {selectedTab.headerExtra}
            </div>
          )}
        </div>
        {tabs.map((chartTab) => (
          <TabsContent
            key={chartTab.id}
            value={chartTab.id}
            className="w-full data-[state=active]:flex data-[state=active]:items-start data-[state=active]:justify-center sm:flex-1 sm:data-[state=active]:items-center"
          >
            <ChartPanel {...chartTab} height={height} />
          </TabsContent>
        ))}
      </Tabs>
    </Panel>
  )
}

interface ChartPanelProps extends ChartTab {
  height: number
}

function ChartPanel({ height, component: Chart, isError, isPending, props }: ChartPanelProps) {
  const [ref, { width }] = useElementSize()

  if (isPending) {
    return (
      <div className="flex items-center justify-center" style={{ height }} ref={ref}>
        <DelayedComponent>
          <Loader2 className="h-8 animate-spin text-neutral-200" data-chromatic="ignore" />
        </DelayedComponent>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center" style={{ height }} ref={ref}>
        <div className="typography-label-3 flex items-center rounded-full bg-secondary px-3 py-1 text-secondary/80">
          <AlertTriangle className="h-4" /> Failed to load chart data
        </div>
      </div>
    )
  }

  return (
    <div ref={ref} className="w-full">
      <Chart {...props} height={height} width={width} />
    </div>
  )
}
